/**
 * Character Sheet Page
 *
 * Purpose: Page wrapper for character detail with data fetching
 * WHO: Anyone with a `/character/:id` link (campaign members and outsiders
 *      alike reach this route; the backend enforces who may actually see data)
 * WHAT: Fetch character data via `GET /api/characters/{id}`, handle loading/
 *       not-found/forbidden/error states, render the contract's actual
 *       fields honestly (no fabricated race/background/alignment/attributes
 *       — those were never real API data; see below).
 * WHERE: Mythbound.App/src/pages/CharacterSheetPage.tsx
 * WHY: Provide a data layer for character detail that matches the FROZEN
 *      backend contract exactly (PRD001A 001A.4).
 *
 * Created: 2026-01-24 for Issue #018a integration
 * Rewritten 2026-09-21 (PRD001A 001A.4 — Character contract half):
 *
 * REMOVED THIS WAVE: the previous version rendered through `CharacterSheet.tsx`
 * (a presentational component built for a fictional full sheet — race,
 * background, alignment, experience, initiative, speed, hitDice, abilities,
 * skills, spells, equipment, features, backstory, notes — NONE of which the
 * real `Character` contract returns) via a `mapCharacterToSheet()` function
 * that FABRICATED every one of those fields (`race: 'Unknown'`,
 * `alignment: 'Neutral'`, `armorClass: char.armor_class ?? 10`, `speed: 30`,
 * empty arrays, etc.) just to satisfy that component's prop shape. That is
 * exactly the "no fabricated defaults" problem PRD001A 001A.4 item 1
 * forbids. This page now renders the CONTRACT's actual fields directly
 * (name, class, level, hp/max_hp, armor_class, lifecycle_conflict) instead
 * of stretching fake data across an unrelated display component.
 * `CharacterSheet.tsx`'s component is left in place (still a valid
 * presentational shell for a future full-sheet feature) but is no longer
 * imported here.
 *
 * Owner controls use the server-computed `is_owner` projection; every write remains authorized by the backend.
 * INTEGRATION POINTS:
 * - CharacterService.getCharacter(id) for fetching
 * - CharacterSheetSkeleton for loading state
 * - NotFoundCharacter for the not-found state
 */

import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { Box, Button, HStack, Text, Heading, SimpleGrid, Badge, VStack } from '@chakra-ui/react';
import { ArrowLeft } from 'lucide-react';
import {
  characterService,
  getCharacterErrorCode,
  classifyCharacterField,
  formatCharacterField,
  type Character,
  type CharacterUpdateRequest,
} from '../services/api/CharacterService';
import { ApiError } from '../services/api/ApiError';
import { CharacterSheetSkeleton } from '../components/character/CharacterSheetSkeleton';
import { NotFoundCharacter } from '../components/character/NotFoundCharacter';
import { theme as mythboundTheme } from '../config/theme';

type PageState = 'loading' | 'ready' | 'not_found' | 'forbidden' | 'error';

// Key detail state to route identity before rendering: an old private sheet/edit draft
// must not flash under a new Character URL while its next read is still pending.
export const CharacterSheetPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return <CharacterSheetDetail key={id ?? 'missing-character'} />;
};

const CharacterSheetDetail: React.FC = () => {
  // Route param is `:id` (see App.tsx's `/character/:id`), not `:characterId`
  // — fixed 2026-09-21 (PRD001A 001A.4 Wave 3) alongside wiring this page
  // into that route for the first time; the previous name here would always
  // have resolved to `undefined` and hit the not-found branch below.
  const { id: characterId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const [character, setCharacter] = useState<Character | null>(null);
  const [pageState, setPageState] = useState<PageState>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editClass, setEditClass] = useState('');
  const [editLevel, setEditLevel] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editPreferences, setEditPreferences] = useState('');
  const [hpInput, setHpInput] = useState('');
  const [maxHpInput, setMaxHpInput] = useState('');
  const [hpError, setHpError] = useState<string | null>(null);
  const [isSavingHp, setIsSavingHp] = useState(false);

  useEffect(() => {
    const controller = new AbortController();

    const fetchCharacter = async () => {
      if (!characterId) {
        setPageState('not_found');
        return;
      }

      setPageState('loading');
      try {
        const data = await characterService.getCharacter(characterId, { signal: controller.signal });
        if (controller.signal.aborted || !mounted.current) return;
        setCharacter(data);
        setHpInput(data.hp == null ? '' : String(data.hp));
        setMaxHpInput(data.max_hp == null ? '' : String(data.max_hp));
        setPageState('ready');
      } catch (error) {
        if (controller.signal.aborted || !mounted.current) return;
        if (error instanceof DOMException && error.name === 'AbortError') return;
        // Distinguish not_found/forbidden/other by ApiError.kind, not by
        // string-matching the message — a previous version of this handler
        // checked `error.message?.includes('404')`, which breaks the moment
        // the fixed contract message text changes.
        if (error instanceof ApiError) {
          if (error.kind === 'not_found') {
            setPageState('not_found');
            return;
          }
          if (error.kind === 'forbidden' || error.kind === 'unauthenticated') {
            setPageState('forbidden');
            setErrorMessage(error.message);
            return;
          }
        }
        setPageState('error');
        setErrorMessage(error instanceof Error ? error.message : 'Failed to load character data');
      }
    };

    void fetchCharacter();
    return () => controller.abort();
  }, [characterId]);

  const isOwner = character?.is_owner === true;
  const preferenceObject = character?.character_preferences && typeof character.character_preferences === 'object'
    && !Array.isArray(character.character_preferences) ? character.character_preferences : {};
  const savedPreferenceNotes = typeof preferenceObject.player_notes === 'string' ? preferenceObject.player_notes : '';

  const startEditing = () => {
    if (!character || !isOwner) return;
    setEditName(character.character_name ?? '');
    setEditClass(character.character_class ?? '');
    setEditLevel(character.level == null ? '' : String(character.level));
    setEditDescription(character.character_description ?? '');
    setEditPreferences(savedPreferenceNotes);
    setEditError(null);
    setIsEditing(true);
  };

  const saveEdits = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!character || !isOwner) return;
    const trimmedName = editName.trim();
    const changedName = editName !== (character.character_name ?? '') && trimmedName !== (character.character_name ?? '');
    const nameLength = Array.from(trimmedName).length;
    if (changedName && (nameLength < 1 || nameLength > 50)) {
      setEditError('Character names must contain 1 to 50 characters.');
      return;
    }

    const changedLevel = editLevel !== (character.level == null ? '' : String(character.level));
    const nextLevel = Number(editLevel);
    if (changedLevel && (!Number.isInteger(nextLevel) || nextLevel < 1 || nextLevel > 20)) {
      setEditError('Level must be a whole number from 1 to 20.');
      return;
    }
    const updates: CharacterUpdateRequest = {};
    if (changedName) updates.character_name = trimmedName;
    if (changedLevel) updates.level = nextLevel;
    const nextClass = editClass.trim() || null;
    if (nextClass !== (character.character_class ?? null)) updates.character_class = nextClass;
    const originalDescription = character.character_description ?? '';
    const nextDescription = editDescription === originalDescription ? character.character_description ?? null : editDescription.trim() || null;
    if (editDescription !== originalDescription && nextDescription !== (character.character_description ?? null)) updates.character_description = nextDescription;
    const nextPreferenceText = editPreferences;
    if (nextPreferenceText !== savedPreferenceNotes) {
      // Preferences replace the stored object, so retain imported/unknown keys
      // and modify only this UI's own player_notes entry.
      updates.character_preferences = { ...preferenceObject, player_notes: nextPreferenceText };
    }
    if (Object.keys(updates).length === 0) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    setEditError(null);
    try {
      const updated = await characterService.updateCharacter(character.character_id, updates);
      if (!mounted.current) return;
      setCharacter(updated);
      setIsEditing(false);
    } catch (error) {
      if (!mounted.current) return;
      const code = getCharacterErrorCode(error);
      setEditError(code === 'duplicate_name'
        ? 'Another active character already uses that name.'
        : error instanceof Error ? error.message : 'Could not save character changes.');
    } finally {
      if (mounted.current) setIsSaving(false);
    }
  };

  const saveHp = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!character || !isOwner) return;
    const hp = Number(hpInput);
    const maxHp = maxHpInput.trim() === '' ? undefined : Number(maxHpInput);
    if (hpInput.trim() === '' || !Number.isInteger(hp) || hp < -100 || hp > 999 ||
        (maxHp !== undefined && (!Number.isInteger(maxHp) || maxHp < 1 || maxHp > 999))) {
      setHpError('Enter current HP from -100 to 999 and an optional maximum from 1 to 999.');
      return;
    }
    setIsSavingHp(true);
    setHpError(null);
    try {
      // HP uses its dedicated owner-only endpoint. Omit an unknown maximum so
      // a paper-sheet value stays unknown rather than being replaced by a default.
      const result = await characterService.updateCharacterHP(character.character_id, {
        hp,
        ...(maxHp === undefined ? {} : { max_hp: maxHp }),
      });
      if (!mounted.current) return;
      setCharacter((current) => current ? { ...current, hp: result.hp, max_hp: result.max_hp } : current);
      setHpInput(String(result.hp));
      setMaxHpInput(result.max_hp == null ? '' : String(result.max_hp));
    } catch (error) {
      if (!mounted.current) return;
      setHpError(error instanceof Error ? error.message : 'Could not save hit points.');
    } finally {
      if (mounted.current) setIsSavingHp(false);
    }
  };

  const archiveOwnedCharacter = async () => {
    if (!character || !isOwner) return;
    if (!window.confirm(`Archive ${character.character_name ?? 'this character'}? You can restore it later.`)) return;
    setEditError(null);
    try {
      const result = await characterService.archiveCharacter(character.character_id);
      // A completed old-route archive must not navigate a different sheet away.
      if (!mounted.current) return;
      window.alert(`${result.character_name} was archived.`);
      // Returning to the campaign route remounts its providers and refetches the shared roster.
      navigate(`/campaigns/${character.campaign_id}`, { replace: true });
    } catch (error) {
      if (!mounted.current) return;
      const code = getCharacterErrorCode(error);
      setEditError(code === 'lifecycle_conflict'
        ? 'This character has conflicting lifecycle data and could not be archived.'
        : error instanceof Error ? error.message : 'Could not archive character.');
    }
  };
  if (pageState === 'loading') {
    return <CharacterSheetSkeleton />;
  }

  if (pageState === 'not_found') {
    return (
      <NotFoundCharacter
        characterId={characterId}
        onCreateNew={() => navigate('/character/new')}
        onGoBack={() => navigate(-1)}
      />
    );
  }

  if (pageState === 'forbidden') {
    return (
      <Box textAlign="center" py={20}>
        <Heading size="md" color={mythboundTheme.colors.primary.gold} mb={2}>Access restricted</Heading>
        <Text color={mythboundTheme.colors.accent.silver}>
          {errorMessage ?? "You don't have permission to view this character."}
        </Text>
        <Button mt={6} leftIcon={<ArrowLeft />} variant="ghost" onClick={() => navigate(-1)}>
          Back
        </Button>
      </Box>
    );
  }

  if (pageState === 'error' || !character) {
    return (
      <Box textAlign="center" py={20}>
        <Heading size="md" color={mythboundTheme.colors.functional.error} mb={2}>Something went wrong</Heading>
        <Text color={mythboundTheme.colors.accent.silver}>{errorMessage ?? 'Failed to load character data.'}</Text>
        <Button mt={6} leftIcon={<ArrowLeft />} variant="ghost" onClick={() => navigate(-1)}>
          Back
        </Button>
      </Box>
    );
  }

  // Explicit server ownership is the only signal that enables owner rendering.
  const hpDisplay = classifyCharacterField(character.hp, isOwner);
  const maxHpDisplay = classifyCharacterField(character.max_hp, isOwner);
  const acDisplay = classifyCharacterField(character.armor_class, isOwner);
  const classDisplay = classifyCharacterField(character.character_class, isOwner);
  const levelDisplay = classifyCharacterField(character.level, isOwner);
  const nameDisplay = classifyCharacterField(character.character_name, isOwner);

  return (
    <Box>
      <HStack spacing={2} mb={6} justify="space-between">
        <Button leftIcon={<ArrowLeft />} variant="ghost" onClick={() => navigate(-1)}>
          Back
        </Button>
      </HStack>

      {character.lifecycle_conflict && (
        <Box bg="rgba(255, 193, 7, 0.12)" border="1px solid #ffc107" borderRadius="md" p={3} mb={4}>
          <Text fontSize="sm" color="#ffc107">
            This character&apos;s stored data is in an inconsistent state. Some information may be unreliable
            until it&apos;s reconciled.
          </Text>
        </Box>
      )}

      <Heading
        mb={2}
        fontFamily={mythboundTheme.typography.fontFamily.heading}
        color={mythboundTheme.colors.primary.gold}
      >
        {formatCharacterField(nameDisplay, (v) => v)}
      </Heading>
      <HStack spacing={3} mb={6}>
        <Text color={mythboundTheme.colors.accent.silver}>{formatCharacterField(classDisplay, (v) => v)}</Text>
        <Badge colorScheme="blue">Level {formatCharacterField(levelDisplay, (v) => String(v))}</Badge>
      </HStack>

      {isOwner && (
        <Box mb={6} p={4} border="1px solid" borderColor={mythboundTheme.colors.accent.brown} borderRadius="md">
          <HStack justify="space-between" mb={3}>
            <Heading size="sm">Your character details</Heading>
            {!isEditing && <Button size="sm" onClick={startEditing}>Edit character</Button>}
          </HStack>
          {isEditing ? (
            <form onSubmit={saveEdits}>
              <VStack align="stretch" spacing={3}>
                <label>Character name<input aria-label="Character name" value={editName} onChange={(event) => setEditName(event.target.value)} /></label>
                <label>Class<input aria-label="Class" value={editClass} onChange={(event) => setEditClass(event.target.value)} /></label>
                <label>Level<input aria-label="Level" type="number" min={1} max={20} value={editLevel} onChange={(event) => setEditLevel(event.target.value)} /></label>
                <label>Backstory<textarea aria-label="Backstory" value={editDescription} onChange={(event) => setEditDescription(event.target.value)} rows={4} /></label>
                <label>Play preferences<textarea aria-label="Play preferences" value={editPreferences} onChange={(event) => setEditPreferences(event.target.value)} rows={3} /></label>
                {editError && <Text role="alert" color={mythboundTheme.colors.functional.error}>{editError}</Text>}
                <HStack>
                  <Button type="submit" isLoading={isSaving}>Save changes</Button>
                  <Button type="button" variant="ghost" onClick={() => { setIsEditing(false); setEditError(null); }}>Cancel</Button>
                </HStack>
              </VStack>
            </form>
          ) : (
            <VStack align="stretch" spacing={2}>
              <Text whiteSpace="pre-wrap">{character.character_description ?? 'No backstory added.'}</Text>
              <Text whiteSpace="pre-wrap">Play preferences: {savedPreferenceNotes || 'None added.'}</Text>
              {character.character_preferences !== undefined && (
                <details><summary>Stored preferences</summary><pre>{JSON.stringify(character.character_preferences, null, 2)}</pre></details>
              )}
              {character.character_sheet && Object.keys(character.character_sheet).length > 0 && (
                <details><summary>Stored sheet data</summary><pre>{JSON.stringify(character.character_sheet, null, 2)}</pre></details>
              )}
              {editError && <Text role="alert" color={mythboundTheme.colors.functional.error}>{editError}</Text>}
              <Button alignSelf="start" colorScheme="red" variant="outline" onClick={() => void archiveOwnedCharacter()}>Archive character</Button>
            </VStack>
          )}
          <form onSubmit={saveHp} style={{ marginTop: '16px' }}>
            <VStack align="stretch" spacing={2}>
              <Heading size="xs">Hit points</Heading>
              <label>Current HP<input aria-label="Current HP" type="number" min={-100} max={999} value={hpInput} onChange={(event) => setHpInput(event.target.value)} /></label>
              <label>Maximum HP<input aria-label="Maximum HP" type="number" min={1} max={999} value={maxHpInput} onChange={(event) => setMaxHpInput(event.target.value)} /></label>
              {hpError && <Text role="alert" color={mythboundTheme.colors.functional.error}>{hpError}</Text>}
              <Button type="submit" alignSelf="start" isLoading={isSavingHp}>Save HP</Button>
            </VStack>
          </form>
        </Box>
      )}
      <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={4}>
        <VStack align="start" bg="rgba(17, 17, 22, 0.7)" borderRadius="md" p={4} spacing={1}>
          <Text fontSize="sm" color={mythboundTheme.colors.accent.silver}>Hit Points</Text>
          <Text fontWeight="bold" color={mythboundTheme.colors.secondary.white}>
            {formatCharacterField(hpDisplay, (v) => String(v))}
            {maxHpDisplay.kind === 'value' ? ` / ${maxHpDisplay.value}` : ''}
          </Text>
        </VStack>
        <VStack align="start" bg="rgba(17, 17, 22, 0.7)" borderRadius="md" p={4} spacing={1}>
          <Text fontSize="sm" color={mythboundTheme.colors.accent.silver}>Armor Class</Text>
          <Text fontWeight="bold" color={mythboundTheme.colors.secondary.white}>
            {formatCharacterField(acDisplay, (v) => String(v))}
          </Text>
        </VStack>
        <VStack align="start" bg="rgba(17, 17, 22, 0.7)" borderRadius="md" p={4} spacing={1}>
          <Text fontSize="sm" color={mythboundTheme.colors.accent.silver}>Created</Text>
          <Text fontWeight="bold" color={mythboundTheme.colors.secondary.white}>
            {character.created_at ? new Date(character.created_at).toLocaleDateString() : 'Not set'}
          </Text>
        </VStack>
      </SimpleGrid>
    </Box>
  );
};

export default CharacterSheetPage;
